import { useEffect, useRef } from 'react';

import { onWsReconnect } from '@api';

// A flaky / rate-limited socket can reconnect many times in a few seconds. Each
// reconnect would otherwise re-fire every registered refetch (notification
// count, deep-lookup, lists…), hammering the API. Cap each consumer to one
// refetch per window — the first reconnect refetches, the rest are coalesced.
const RECONNECT_THROTTLE_MS = 15_000;

/**
 * Register `callback` to fire whenever the WebSocket reconnects (not on the
 * first connection). Auto-cleans up on unmount.
 *
 * Per ADR-009, every subscription hook must refetch its underlying query on
 * every reconnect. Pass the existing initial-fetch / refetch function as the
 * callback — the registration handles the rest. The callback is leading-edge
 * throttled so a reconnect storm doesn't trigger a refetch storm.
 *
 * See docs/specs/subscriptions-adr-009-compliance.md.
 */
export const useWsReconnect = (callback: () => void): void => {
  // Keep the latest callback without re-registering on every identity change.
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    let lastRun = 0;
    const throttled = (): void => {
      const now = Date.now();
      if (now - lastRun < RECONNECT_THROTTLE_MS) return;
      lastRun = now;
      callbackRef.current();
    };
    const unregister = onWsReconnect(throttled);
    return unregister;
  }, []);
};

export default useWsReconnect;
